import {
	AfterViewInit, Component, ElementRef, EventEmitter, forwardRef, Input, NgZone, OnDestroy, Output, ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Editor, NodeViewRendererProps } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import BaseImage from '@tiptap/extension-image';
import BaseTable from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';

declare let Enkora : { tt : (a : string) => string };

function tt(text : string) : string
{
	return (typeof Enkora !== 'undefined' && Enkora && typeof Enkora.tt === 'function') ? Enkora.tt(text) : text;
}

type ProseMirrorNode = NodeViewRendererProps['node'];

function sizeAttributes(defaults : { width? : string } = {})
{
	return {
		width  : sizeAttribute('width', defaults.width),
		height : sizeAttribute('height', null),
	};
}

function sizeAttribute(name : 'width' | 'height', default_value : string | null)
{
	return {
		default    : default_value || null,
		parseHTML  : (el : HTMLElement) => withUnit(el.style[name] || el.getAttribute(name)),
		renderHTML : (attrs : Record<string, string>) => {
			if (!attrs[name]) return {};

			const rendered : Record<string, string> = { style: `${name}: ${attrs[name]}` };
			const plain = withoutUnit(attrs[name]);
			if (plain) rendered[name] = plain;

			return rendered;
		},
	};
}

function withUnit(value : string | null) : string | null
{
	if (!value) return null;

	return /^\d+(\.\d+)?$/.test(value) ? value + 'px' : value;
}

function withoutUnit(value : string) : string | null
{
	const m = String(value).match(/^(\d+(?:\.\d+)?)(px|%)?$/);
	if (!m) return null;

	return m[2] === '%' ? m[1] + '%' : m[1];
}

function imageNodeView({ node, editor, getPos } : NodeViewRendererProps)
{
	let current = node;

	const dom = document.createElement('div');
	dom.className = 'nexus-image';
	const img = document.createElement('img');
	const grip = document.createElement('span');
	grip.className = 'nexus-image-grip';
	dom.appendChild(img);
	dom.appendChild(grip);

	const render = (n : ProseMirrorNode) => {
		current = n;
		img.setAttribute('src', n.attrs['src'] || '');
		img.setAttribute('alt', n.attrs['alt'] || '');
		if (n.attrs['title']) img.setAttribute('title', n.attrs['title']);
		else img.removeAttribute('title');
		img.style.width = n.attrs['width'] || '';
		img.style.height = n.attrs['height'] || '';
	};

	grip.addEventListener('mousedown', (event : MouseEvent) => {
		event.preventDefault();

		const start = img.getBoundingClientRect();
		const in_percent = String(current.attrs['width'] || '').endsWith('%');
		const container = dom.parentElement ? dom.parentElement.clientWidth : 0;
		const keep_height = !!current.attrs['height'];

		const onMove = (e : MouseEvent) => {
			const width = Math.max(MIN_IMAGE_SIZE, start.width + e.clientX - start.right);
			img.style.width = in_percent && container
				? Math.round(width / container * 100) + '%'
				: Math.round(width) + 'px';
			img.style.height = keep_height ? Math.round(start.height * width / start.width) + 'px' : '';
		};

		const onUp = () => {
			document.removeEventListener('mousemove', onMove, true);
			document.removeEventListener('mouseup', onUp, true);

			const pos = getPos();
			if (typeof pos !== 'number') return;

			editor.chain().setNodeSelection(pos).updateAttributes(current.type.name, {
				width  : img.style.width || null,
				height : img.style.height || null,
			}).run();
		};

		document.addEventListener('mousemove', onMove, true);
		document.addEventListener('mouseup', onUp, true);
	});

	render(node);

	return {
		dom,
		update : (updated : ProseMirrorNode) => {
			if (updated.type !== current.type) return false;

			render(updated);

			return true;
		},
		ignoreMutation : () => true,
		stopEvent      : (event : Event) => event.target === grip,
	};
}

const SizedImage = BaseImage.extend({
	addAttributes() {
		return { ...this.parent?.(), ...sizeAttributes() };
	},
	addNodeView() {
		return imageNodeView;
	},
});

const SizedTable = BaseTable.extend({
	addAttributes() {
		return { ...this.parent?.(), ...sizeAttributes({ width: '100%' }) };
	},
});

interface ResizeTarget {
	type : 'image' | 'table';
	dom : HTMLElement;
}

const GRIP_SIZE = 18;

const MIN_IMAGE_SIZE = 24;

const RESIZE_HEIGHT = 38;
const RESIZE_HEIGHT_WITH_ALT = 70;

@Component({
	selector    : 'app-rich-text-editor',
	standalone  : true,
	imports     : [CommonModule, FormsModule],
	templateUrl : './rich-text-editor.component.html',
	styleUrls   : ['./rich-text-editor.component.scss'],
	providers   : [{
		provide     : NG_VALUE_ACCESSOR,
		useExisting : forwardRef(() => RichTextEditorComponent),
		multi       : true,
	}],
})
export class RichTextEditorComponent implements AfterViewInit, OnDestroy, ControlValueAccessor {

	@Input() enableImages = false;
	@Input() minHeight = 100;
	@Input() maxHeight = 500;
	@Input() showSource = true;

	@Output() editorFocus = new EventEmitter<void>();

	@ViewChild('contentEl', { static: true }) contentEl : ElementRef<HTMLElement>;
	@ViewChild('rteEl', { static: true }) rteEl : ElementRef<HTMLElement>;
	@ViewChild('popupsEl', { static: true }) popupsEl : ElementRef<HTMLElement>;

	readonly t = {
		paragraphStyle : tt('Paragraph style'),
		paragraph      : tt('Paragraph'),
		bold           : tt('Bold'),
		italic         : tt('Italic'),
		quote          : tt('Quote'),
		bulletedList   : tt('Bulleted list'),
		numberedList   : tt('Numbered list'),
		list           : tt('List'),
		link           : tt('Link'),
		image          : tt('Image'),
		insertTable    : tt('Insert table'),
		undo           : tt('Undo'),
		redo           : tt('Redo'),
		visual         : tt('Visual'),
		source         : tt('Source'),
		openInNewTab   : tt('Open in new tab'),
		apply          : tt('Apply'),
		remove         : tt('Remove'),
		width          : tt('Width'),
		height         : tt('Height'),
		maximize       : tt('Maximize'),
		ok             : tt('Ok'),
		imageUrl       : tt('Image URL'),
		insert         : tt('Insert'),
		altText        : tt('Alternative text'),
	};

	editor : Editor | null = null;
	source_mode = false;
	source_html = '';
	maximized = false;

	active = { bold: false, italic: false, blockquote: false, bulletList: false, orderedList: false, link: false };
	heading_value = 'p';

	readonly GRID_MAX = 10;
	readonly grid_range = Array.from({ length: 10 }, (_, i) => i);

	table_grid_open = false;
	grid_rows = 0;
	grid_cols = 0;
	grid_focus_row = 0;
	grid_focus_col = 0;
	grid_pos = { top: 0, left: 0 };

	link_panel_open = false;
	link_url = '';
	link_new_tab = true;
	link_pos = { top: 0, left: 0 };

	image_panel_open = false;
	image_url = '';
	image_alt = '';
	image_width = '';
	image_width_unit = 'px';
	image_height = '';
	image_height_unit = 'px';
	image_pos = { top: 0, left: 0 };

	resize_target : ResizeTarget | null = null;
	resize_alt = '';
	resize_width = '';
	resize_width_unit = 'px';
	resize_height = '';
	resize_height_unit = 'px';
	resize_pos = { top: 0, left: 0 };

	private value = '';
	is_disabled = false;
	private dragged_size : { height : string; max_height : string } | null = null;
	private onChange : (value : string) => void = () => {};
	private onTouched : () => void = () => {};

	constructor(private zone : NgZone, private host : ElementRef<HTMLElement>) {}

	ngAfterViewInit() : void
	{
		this.editor = new Editor({
			element    : this.contentEl.nativeElement,
			extensions : [
				StarterKit,
				Link.configure({ openOnClick: false }),
				SizedImage,
				SizedTable,
				TableRow,
				TableHeader,
				TableCell,
			],
			content    : this.value,
			editable   : !this.is_disabled,
			onUpdate   : () => this.zone.run(() => {
				this.value = this.editor.getHTML();
				this.onChange(this.value);
			}),
			onSelectionUpdate : () => this.zone.run(() => this.refreshState()),
			onTransaction     : () => this.zone.run(() => this.refreshState()),
			onFocus           : () => this.zone.run(() => this.editorFocus.emit()),
			onBlur            : () => this.onTouched(),
		});
		this.refreshState();

		document.body.appendChild(this.popupsEl.nativeElement);

		document.addEventListener('click', this.onDocumentClick, true);
		window.addEventListener('wheel', this.onWheel, { capture: true, passive: true });
	}

	insertText(text : string) : void
	{
		this.editor?.chain().focus().insertContent(text).run();
	}

	ngOnDestroy() : void
	{
		document.removeEventListener('click', this.onDocumentClick, true);
		document.removeEventListener('keydown', this.onMaximizedKeydown, true);
		window.removeEventListener('wheel', this.onWheel, true);
		// Both hang off <body>, so the view teardown would not reach them from the host element.
		this.popupsEl.nativeElement.remove();
		if (this.maximized) this.host.nativeElement.appendChild(this.rteEl.nativeElement);
		this.editor?.destroy();
	}

	toggleMaximized() : void
	{
		this.maximized = !this.maximized;
		this.hideResize();

		// The expanded panel is position:fixed, which anchors to the nearest transformed ancestor
		// (a dragged modal, a Material dialog) rather than the viewport — so it edits from <body>.
		if (this.maximized) document.body.appendChild(this.rteEl.nativeElement);
		else this.host.nativeElement.appendChild(this.rteEl.nativeElement);

		const pm = this.proseMirrorEl();
		if (this.maximized) {
			if (pm) {
				this.dragged_size = { height: pm.style.height, max_height: pm.style.maxHeight };
				pm.style.height = '';
				pm.style.maxHeight = '';
			}
			document.addEventListener('keydown', this.onMaximizedKeydown, true);
		}
		else {
			if (pm && this.dragged_size) {
				pm.style.height = this.dragged_size.height;
				pm.style.maxHeight = this.dragged_size.max_height;
			}
			this.dragged_size = null;
			document.removeEventListener('keydown', this.onMaximizedKeydown, true);
		}

		if (!this.source_mode) this.editor?.commands.focus();
	}

	private onMaximizedKeydown = (event : KeyboardEvent) : void =>
	{
		if (event.key !== 'Escape') return;

		event.stopPropagation();
		this.zone.run(() => this.toggleMaximized());
	};

	onContentPointerDown(event : PointerEvent) : void
	{
		if (this.maximized) return;

		const pm = (event.target as HTMLElement)?.closest?.('.ProseMirror') as HTMLElement | null;
		if (!pm) return;

		const rect = pm.getBoundingClientRect();
		if (rect.right - event.clientX > GRIP_SIZE || rect.bottom - event.clientY > GRIP_SIZE) return;

		pm.style.height = rect.height + 'px';
		pm.style.maxHeight = 'none';
	}

	private proseMirrorEl() : HTMLElement | null
	{
		return this.contentEl.nativeElement.querySelector<HTMLElement>('.ProseMirror');
	}

	writeValue(value : string) : void
	{
		this.value = value || '';
		if (this.editor && this.value !== this.editor.getHTML()) {
			this.editor.commands.setContent(this.value, false);
		}
	}

	registerOnChange(fn : (value : string) => void) : void
	{
		this.onChange = fn;
	}

	registerOnTouched(fn : () => void) : void
	{
		this.onTouched = fn;
	}

	setDisabledState(disabled : boolean) : void
	{
		this.is_disabled = disabled;
		this.editor?.setEditable(!disabled);
		if (disabled) {
			this.closePanels();
			this.hideResize();
		}
	}

	setHeading() : void
	{
		if (this.heading_value === 'p') this.editor.chain().focus().setParagraph().run();
		else this.editor.chain().focus().toggleHeading({ level: parseInt(this.heading_value, 10) as 1 | 2 | 3 }).run();
	}

	toggleBold() : void { this.editor.chain().focus().toggleBold().run(); }
	toggleItalic() : void { this.editor.chain().focus().toggleItalic().run(); }
	toggleQuote() : void { this.editor.chain().focus().toggleBlockquote().run(); }
	toggleBulletList() : void { this.editor.chain().focus().toggleBulletList().run(); }
	toggleOrderedList() : void { this.editor.chain().focus().toggleOrderedList().run(); }
	undo() : void { this.editor.chain().focus().undo().run(); }
	redo() : void { this.editor.chain().focus().redo().run(); }

	private refreshState() : void
	{
		if (!this.editor) return;

		this.active.bold = this.editor.isActive('bold');
		this.active.italic = this.editor.isActive('italic');
		this.active.blockquote = this.editor.isActive('blockquote');
		this.active.bulletList = this.editor.isActive('bulletList');
		this.active.orderedList = this.editor.isActive('orderedList');
		this.active.link = this.editor.isActive('link');
		if (this.editor.isActive('heading', { level: 1 })) this.heading_value = '1';
		else if (this.editor.isActive('heading', { level: 2 })) this.heading_value = '2';
		else if (this.editor.isActive('heading', { level: 3 })) this.heading_value = '3';
		else this.heading_value = 'p';
	}

	private closePanels() : void
	{
		this.table_grid_open = false;
		this.link_panel_open = false;
		this.image_panel_open = false;
	}

	toggleTableGrid(event : MouseEvent) : void
	{
		const was_open = this.table_grid_open;
		this.closePanels();
		if (was_open) return;

		this.grid_rows = 0;
		this.grid_cols = 0;
		this.grid_focus_row = 0;
		this.grid_focus_col = 0;
		this.grid_pos = this.anchorBelow(event.currentTarget as HTMLElement, 190);
		this.table_grid_open = true;
	}

	hoverGrid(row : number, col : number) : void
	{
		this.grid_rows = row + 1;
		this.grid_cols = col + 1;
	}

	/**
	 * The picker is a 10×10 grid, so every cell being tabbable would put 100 stops between the
	 * toolbar and the rest of the form. Only the cell matching the current size is in the tab
	 * order; the arrow keys move it, which is the usual way a grid of controls is navigated.
	 */
	onGridKeydown(event : KeyboardEvent, row : number, col : number) : void
	{
		const moves : Record<string, [number, number]> = {
			ArrowUp    : [-1, 0],
			ArrowDown  : [1, 0],
			ArrowLeft  : [0, -1],
			ArrowRight : [0, 1],
		};
		const move = moves[event.key];
		if (!move) return;

		event.preventDefault();
		const last = this.grid_range.length - 1;
		this.grid_focus_row = Math.min(last, Math.max(0, row + move[0]));
		this.grid_focus_col = Math.min(last, Math.max(0, col + move[1]));
		this.hoverGrid(this.grid_focus_row, this.grid_focus_col);

		const selector = `.rte__tablegrid-cell[data-row="${this.grid_focus_row}"]`
			+ `[data-col="${this.grid_focus_col}"]`;
		this.popupsEl.nativeElement.querySelector<HTMLElement>(selector)?.focus();
	}

	pickGrid(row : number, col : number) : void
	{
		this.editor.chain().focus().insertTable({ rows: row + 1, cols: col + 1, withHeaderRow: true }).run();
		this.table_grid_open = false;
	}

	toggleLinkPanel(event : MouseEvent) : void
	{
		const was_open = this.link_panel_open;
		this.closePanels();
		if (was_open) return;

		const attrs = this.editor.getAttributes('link');
		this.link_url = attrs['href'] || '';
		this.link_new_tab = attrs['target'] ? attrs['target'] === '_blank' : true;
		this.link_pos = this.anchorBelow(event.currentTarget as HTMLElement, 340);
		this.link_panel_open = true;
	}

	applyLink() : void
	{
		const url = this.link_url.trim();
		if (!url) {
			this.editor.chain().focus().extendMarkRange('link').unsetLink().run();
			this.link_panel_open = false;

			return;
		}

		const target = this.link_new_tab ? '_blank' : null;
		if (this.editor.state.selection.empty) {
			this.editor.chain().focus().insertContent({
				type  : 'text',
				text  : url,
				marks : [{ type: 'link', attrs: { href: url, target } }],
			}).run();
		}
		else {
			this.editor.chain().focus().extendMarkRange('link').setLink({ href: url, target }).run();
		}
		this.link_panel_open = false;
	}

	removeLink() : void
	{
		this.editor.chain().focus().extendMarkRange('link').unsetLink().run();
		this.link_panel_open = false;
	}

	toggleImagePanel(event : MouseEvent) : void
	{
		const was_open = this.image_panel_open;
		this.closePanels();
		if (was_open) return;

		const attrs = this.editor.isActive('image') ? this.editor.getAttributes('image') : {};
		this.image_url = attrs['src'] || '';
		this.image_alt = attrs['alt'] || '';
		const w = this.parseDim(attrs['width']);
		const h = this.parseDim(attrs['height']);
		this.image_width = w.num;
		this.image_width_unit = w.unit;
		this.image_height = h.num;
		this.image_height_unit = h.unit;
		this.image_pos = this.anchorBelow(event.currentTarget as HTMLElement, 250);
		this.image_panel_open = true;
	}

	applyImage() : void
	{
		const src = this.image_url.trim();
		if (!src) return;

		const attrs = {
			src,
			alt    : this.image_alt.trim() || null,
			width  : this.image_width !== '' ? this.image_width + this.image_width_unit : null,
			height : this.image_height !== '' ? this.image_height + this.image_height_unit : null,
		};
		if (this.editor.isActive('image')) this.editor.chain().focus().updateAttributes('image', attrs).run();
		else this.editor.chain().focus().setImage(attrs).run();

		this.image_panel_open = false;
	}

	private onDocumentClick = (event : MouseEvent) : void =>
	{
		if (!this.editor) return;

		const el = event.target as HTMLElement;
		if (el?.closest?.('.rte__resize')) return;

		this.zone.run(() => {
			const target = this.contentEl.nativeElement.contains(el) ? this.currentResizeTarget() : null;
			if (target) {
				this.showResize(target);

				return;
			}

			this.hideResize();
		});
	};

	private onWheel = (event : WheelEvent) : void =>
	{
		if (!this.resize_target) return;
		if ((event.target as HTMLElement)?.closest?.('.rte__resize')) return;

		this.zone.run(() => this.hideResize());
	};

	private showResize(target : ResizeTarget) : void
	{
		if (this.is_disabled) return;

		const attrs = this.editor.getAttributes(target.type);
		const w = this.parseDim(attrs['width']);
		const h = this.parseDim(attrs['height']);
		this.resize_width = w.num;
		this.resize_width_unit = w.unit;
		this.resize_height = h.num;
		this.resize_height_unit = h.unit;
		this.resize_alt = attrs['alt'] || '';
		this.resize_target = target;
		this.resize_pos = this.anchorAbove(target);
	}

	private hideResize() : void
	{
		this.resize_target = null;
	}

	applyResize() : void
	{
		if (!this.resize_target || this.is_disabled) return;

		const w = this.resize_width !== '' ? this.resize_width + this.resize_width_unit : null;
		const h = this.resize_height !== '' ? this.resize_height + this.resize_height_unit : null;
		const attrs : Record<string, string> = { width: w, height: h };
		if (this.resize_target.type === 'image') attrs['alt'] = this.resize_alt.trim() || null;
		this.editor.chain().focus().updateAttributes(this.resize_target.type, attrs).run();

		if (this.resize_target.type === 'table') {
			const dom = this.resize_target.dom;
			if (w) dom.style.setProperty('width', w, 'important');
			else dom.style.removeProperty('width');
			if (h) dom.style.setProperty('height', h, 'important');
			else dom.style.removeProperty('height');
		}

		this.resize_pos = this.anchorAbove(this.resize_target);
	}

	private currentResizeTarget() : ResizeTarget | null
	{
		if (this.editor.isActive('image')) {
			const dom = this.editor.view.nodeDOM(this.editor.state.selection.from) as HTMLElement;

			return dom && dom.nodeType === 1 ? { type: 'image', dom } : null;
		}
		if (this.editor.isActive('table')) {
			const at = this.editor.view.domAtPos(this.editor.state.selection.from);
			let node = at && at.node as HTMLElement;
			if (node && node.nodeType !== 1) node = node.parentElement;
			const table = node ? node.closest('table') as HTMLElement : null;

			return table ? { type: 'table', dom: table } : null;
		}

		return null;
	}

	private parseDim(value : string) : { num : string; unit : string }
	{
		const m = String(value || '').match(/^(\d+(?:\.\d+)?)\s*(px|%)?$/);
		if (!m) return { num: '', unit: 'px' };

		return { num: m[1], unit: m[2] || 'px' };
	}

	setSourceMode(on : boolean) : void
	{
		if (on) {
			this.hideResize();
			this.source_html = this.editor.getHTML();
		}
		else {
			this.editor.commands.setContent(this.source_html, true);
		}
		this.source_mode = on;
	}

	/**
	 * Guarded as well as bound to `[disabled]` in the template: the binding is the affordance, but
	 * this is what actually keeps a disabled control's value from being rewritten.
	 */
	onSourceInput() : void
	{
		if (this.is_disabled) return;

		this.value = this.source_html;
		this.onChange(this.value);
	}

	private anchorBelow(el : HTMLElement, width : number) : { top : number; left : number }
	{
		const rect = el.getBoundingClientRect();
		let left = rect.left;
		if (left + width > window.innerWidth - 4) left = window.innerWidth - width - 4;
		if (left < 4) left = 4;

		return { top: rect.bottom + 6, left };
	}

	private anchorAbove(target : ResizeTarget) : { top : number; left : number }
	{
		const rect = target.dom.getBoundingClientRect();
		const height = target.type === 'image' ? RESIZE_HEIGHT_WITH_ALT : RESIZE_HEIGHT;
		let left = rect.left;
		if (left < 4) left = 4;
		let top = rect.top - height - 6;
		if (top < 4) top = rect.bottom + 6;

		return { top, left };
	}
}
